import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Eye, AlertCircle, CheckCircle2, Clock, Plus } from 'lucide-react'
import axiosInstance from '../../services/axiosInstance'
import PageHeader from '../../components/common/PageHeader'
import CreateStudentModal from '../../components/modals/CreateStudentModal'
import { useAuth } from '../../contexts/AuthContext'
import { isAgent } from '../../utils/roleUtils'
import './styles.scss'

export default function Applications() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    fetchApplications()
  }, [])

  const fetchApplications = async () => {
    try {
      setLoading(true)
      // Call our newly added admin endpoint
      const response = await axiosInstance.get('/api/applications/admin/all')
      if (response.data.success) {
        setApplications(response.data.data)
      } else {
        setError(response.data.message || 'Failed to fetch applications')
      }
    } catch (err) {
      setError(err.message || 'Error connecting to server')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateApplication = async (data) => {
    try {
      await axiosInstance.post('/api/applications/agent/create', data)
      setSuccessMsg('Application successfully created! Login credentials have been emailed to the student.')
      fetchApplications()
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to create application.')
      setTimeout(() => setErrorMsg(''), 5000)
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'SUBMITTED':
        return <span className="badge badge--warning"><Clock size={14} /> Submitted</span>
      case 'UNDER_REVIEW':
        return <span className="badge badge--primary"><Eye size={14} /> In Review</span>
      case 'ADMITTED':
        return <span className="badge badge--success"><CheckCircle2 size={14} /> Admitted</span>
      case 'REJECTED':
        return <span className="badge badge--danger"><AlertCircle size={14} /> Rejected</span>
      default:
        return <span className="badge badge--neutral">{status}</span>
    }
  }

  const filteredApps = applications.filter(app => {
    const searchString = `${app.application.id} ${app.application.applicationNumber} ${app.application.status} ${app.studentName}`.toLowerCase()
    const matchesSearch = searchString.includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'ALL' || app.application.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const exportToCSV = () => {
    if (filteredApps.length === 0) return

    const headers = ['App ID', 'Student Name', 'Student Email', 'Citizenship', 'Program', 'Status', 'Date Submitted']
    
    const rows = filteredApps.map(app => [
      app.application.applicationNumber || '',
      `"${(app.studentName || 'Unknown Student').replace(/"/g, '""')}"`,
      `"${(app.studentEmail || '').replace(/"/g, '""')}"`,
      `"${(app.studentCitizenship || '').replace(/"/g, '""')}"`,
      `"${(app.programName || app.application.programId || '').replace(/"/g, '""')}"`,
      app.application.status || '',
      new Date(app.application.createdAt).toLocaleDateString()
    ])

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `applications_export_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="applications-page">
      <PageHeader
        title="Applications"
        subtitle="Manage and review all student applications"
      />

      {successMsg && (
        <div className="alert alert-success" style={{ margin: '0 1.5rem 1.5rem' }}>
          <CheckCircle2 size={18} /> {successMsg}
        </div>
      )}

      {errorMsg && (
        <div className="alert alert-danger" style={{ margin: '0 1.5rem 1.5rem' }}>
          <AlertCircle size={18} /> {errorMsg}
        </div>
      )}

      <div className="glass-panel table-container">
        <div className="table-controls">
          <div className="search-bar">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by ID or Status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-actions-group">
            <select
              className="status-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="APPLICATION_STARTED">Application Started</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="UNDER_REVIEW">In Review</option>
              <option value="ADMITTED">Admitted</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <button 
              className="btn btn-outline export-btn" 
              onClick={exportToCSV} 
              disabled={filteredApps.length === 0}
            >
              Export CSV
            </button>
            {isAgent(user) && user?.organizationIds && user.organizationIds.length > 0 && (
              <button 
                className="btn btn-primary" 
                onClick={() => setIsCreateModalOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', fontWeight: 600, height: '42px' }}
              >
                <Plus size={18} />
                Start New Application
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="table-loading">Loading applications...</div>
        ) : error ? (
          <div className="table-error">{error}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>App ID</th>
                <th>Student</th>
                <th>Citizenship</th>
                <th>Program</th>
                <th>Status</th>
                <th>Date Submitted</th>
                <th>Messages</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApps.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">No applications found.</td>
                </tr>
              ) : (
                filteredApps.map(app => (
                  <tr
                    key={app.application.id}
                    className="clickable-row"
                    onClick={() => navigate(`/applications/${app.application.id}`)}
                  >
                    <td className="font-medium">{app.application.applicationNumber}</td>
                    <td>
                      <div className="student-info-cell">
                        <span className="student-name">{app.studentName || 'Unknown Student'}</span>
                        <span className="student-email">{app.studentEmail}</span>
                      </div>
                    </td>
                    <td>{app.studentCitizenship || '-'}</td>
                    <td>{app.programName || app.application.programId}</td>
                    <td>{getStatusBadge(app.application.status)}</td>
                    <td>{new Date(app.application.createdAt).toLocaleDateString()}</td>
                    <td>
                      {app.unreadMessages > 0 ? (
                        <div className="unread-badge">
                          <span className="badge-pulse"></span>
                          {app.unreadMessages} New
                        </div>
                      ) : (
                        <span className="no-new-messages">No new</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        title="View Details"
                        onClick={() => navigate(`/applications/${app.application.id}`)}
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <CreateStudentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateApplication}
      />
    </div>
  )
}
